import { useCallback, useState } from 'react';
import { useAppDispatch } from 'shared/lib/store';
import { getPageFromNext } from '../helpers/pageHelper';
import { AsyncThunkAction } from '@reduxjs/toolkit';
import { Filters, PaginationHook, TResetFilterParams } from '../types';
import { processFilters } from '../helpers/process';

export function usePagination(
	fetchData: (filters: Filters) => AsyncThunkAction<any, any, any>,
): PaginationHook {
	const dispatch = useAppDispatch();
	const [filters, setFilters] = useState<Filters>({ page: 1 });
	const [displayFilters, setDisplayFilters] = useState<Filters>({ page: 1 });
	const [nextPage, setNextPage] = useState(0);
	const [prevNextPage, setPrevNextPage] = useState(0);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	const setCurrentFilters = useCallback(
		(filtersParam?: Filters) => {
			let currentFilters = filters;
			if (filtersParam) {
				currentFilters = filtersParam;
				setFilters(prevFilters => ({
					...prevFilters,
					...currentFilters,
				}));
			}

			return currentFilters;
		},
		[filters],
	);

	const setNextPageInFilters = (nextPage: number | false | undefined) => {
		if (nextPage) {
			setFilters(prevFilters => ({
				...prevFilters,
				page: nextPage,
			}));
			setNextPage(nextPage);
		} else {
			setNextPage(0);
		}
	};

	const fetchDataWithDispatch = useCallback(
		async (filtersParam?: Filters) => {
			// setIsLoading(true);
			try {
				const currentFilters = setCurrentFilters(filtersParam);
				setDisplayFilters(currentFilters);

				const response: any = await dispatch(fetchData(currentFilters));
				const nextPage = getPageFromNext(response.payload.next ?? '');

				setNextPageInFilters(nextPage);
				setError(null);
				
			} catch (err) {
				setError(err as Error);
			} finally {
				setIsLoading(false);
			}
		},
		[dispatch, fetchData, setCurrentFilters],
	);

	const loadMore = async () => {
		if (!nextPage || nextPage === prevNextPage) {
			return;
		}
		setIsLoadingMore(true);
		setPrevNextPage(nextPage);
		await fetchDataWithDispatch();
		setIsLoadingMore(false);
	};

	const updateFilters = (newFilters: Partial<Filters>) => {
		setFilters(prevFilters => ({ ...prevFilters, ...newFilters, page: 1 }));
	};

	const resetFilters = async ({
		newFilters = {},
		withRequest = true,
	}: TResetFilterParams) => {
		setFilters({ ...newFilters, page: 1 });
		if (withRequest) {
			fetchDataWithDispatch({ ...newFilters, page: 1 });
		}
	};

	return {
		isLoading,
		error,
		filters,
		isLoadingMore,
		displayFilters,
		updateFilters,
		fetchDataWithDispatch,
		loadMore,
		resetFilters,
		processFilters,
	};
}
